import React, { isValidElement } from 'react'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import PropTypes from 'prop-types'

import HubspotScheduler from '@anvilco-pkg/customer-data/hubspot/components/HubspotScheduler'

import { H3 } from 'lib/ui/primitives'
import media from 'media'
import theme from 'theme'

import Section from 'components/page/Section'
import SubSection from 'components/page/Section/SubSection'
import UniversalLink from 'components/UniversalLink'

import { APP_URL, SCROLL_NAME_CONTACT_SALES } from 'helpers'

const IFRAME_CONTACT_SALES_ID = 'solutions-contact-sales-embed'

const StyledSignupSection = styled(Box).attrs({
  mt: [15, 15, 30],
})`
  background: ${theme.colors.blues[10]};
`

const SignupContainer = styled(Flex).attrs({
  flexDirection: ['column'],
  fontSize: [16, 16, 18],
})``

const LeftSide = styled(Flex).attrs({
  width: [1, 1, 0.5],
  flexDirection: ['column'],
  justifyContent: ['center'],
})`
  ${SignupContainer} {
    display: none;
  }

  ${media.desktop`
    ${SignupContainer} {
      display: flex;
    }
  `}
`

const RightSide = styled(Box)
  .attrs({
    width: [1, 1, 0.5],
  })
  .withConfig({
    shouldForwardProp: (prop) => ['customEmbedForm'].indexOf(prop) === -1,
  })`
  ${SignupContainer} {
    margin-top: 24px;
  }

  ${media.desktop`
    ${SignupContainer} {
      display: none;
    }
  `}

  ${({ customEmbedForm }) =>
    !customEmbedForm &&
    `
      iframe {
        height: 635px !important;
      }
    `}
`

const SignupTitle = styled(H3).attrs({ as: 'h2' })`
  font-weight: ${theme.fontWeights.bold};
`

export const SignupSubtitle = styled(Box).attrs({
  fontSize: [16, 16, 18],
  maxWidth: [null, null, 520],
  mt: [24, 24, 36],
})``

const SignupList = styled.ul`
  margin-top: 24px;
  margin-bottom: 24px;

  ${media.desktop`
    margin-top: 36px;
    margin-bottom: 36px;
  `}
`

const SignupFooter = styled.div`
  font-size: 16px;
  line-height: 1.5;
`

export default function ContactAnvil ({
  scrollName = SCROLL_NAME_CONTACT_SALES,
  title = (
    <SignupTitle>
      Get a demo
      <br />
      (from a real person)
    </SignupTitle>
  ),
  description = 'Schedule some time on our calendar to talk through your specific use case and see which Anvil products can help.',
  bullets = [],
  meetingName = 'Contact Sales',
  fullWidth = false,
  hideSignup = false,
  renderEmbedForm = undefined,
  renderAs = 'Section',
  ...others
}) {
  const titleElement = isValidElement(title) ? (
    title
  ) : (
    <SignupTitle>{title}</SignupTitle>
  )

  const Wrapper = renderAs === 'Section' ? Section : SubSection

  const SignupSection = styled(Wrapper)`
    display: flex;
    flex-direction: column;

    ${media.desktop`
    flex-direction: row;
  `}

    &&& {
      ${(props) => (props.fullWidth ? `box-shadow: none` : '')};
    }
  `

  const signupElement = (
    <SignupContainer>
      <strong>Want to try Anvil first?</strong>
      <SignupFooter>
        <UniversalLink to={`${APP_URL}/signup/`}>
          Sign up for free
        </UniversalLink>{' '}
        {/* or <UniversalLink to={`${APP_URL}/signup/`}>try it now</UniversalLink>{' '}
        with any document. */}
      </SignupFooter>
    </SignupContainer>
  )

  const content = (
    <SignupSection
      fullWidth={fullWidth}
      background={theme.colors.greens[20]}
      scrollName={scrollName}
      {...others}
    >
      <LeftSide>
        {titleElement}
        <SignupSubtitle>
          <div>{description}</div>
          <SignupList>
            {bullets.map((bullet, index) => (
              <li key={bullet}>{bullet}</li>
            ))}
          </SignupList>
          {!hideSignup && signupElement}
        </SignupSubtitle>
      </LeftSide>
      <RightSide customEmbedForm={!!renderEmbedForm}>
        {renderEmbedForm ? (
          renderEmbedForm()
        ) : (
          <HubspotScheduler
            meetingName={meetingName}
            id={IFRAME_CONTACT_SALES_ID}
          />
        )}

        {signupElement}
      </RightSide>
    </SignupSection>
  )

  if (fullWidth) {
    return <StyledSignupSection>{content}</StyledSignupSection>
  }

  return content
}

ContactAnvil.propTypes = {
  scrollName: PropTypes.string.isRequired,
  title: PropTypes.node,
  description: PropTypes.string,
  bullets: PropTypes.arrayOf(PropTypes.string),
  meetingName: PropTypes.string,
  fullWidth: PropTypes.bool,
  hideSignup: PropTypes.bool,
  renderEmbedForm: PropTypes.func,
  renderAs: PropTypes.oneOf(['Section', 'SubSection']),
}
